import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack, VStack } from '@components/base/layout';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { modulesService } from '../../api/modulesService';
import type { Module } from '../../api/modulesService';

export function ModulesListPage() {
  const navigate = useNavigate();

  const listPage = useAdminListPage<Module, string>({
    queryKey: ['admin', 'modules'],
    queryFn: () => modulesService.list(),
    deleteFn: (slug) => modulesService.remove(slug),
    getDeleteId: (module) => module.slug,
  });

  return (
    <AdminListPage<Module>
      title="Modules"
      loadingLabel="Loading modules..."
      errorTitle="Failed to load modules"
      errorFallback="An unexpected error occurred."
      showTitleOnError
      emptyTitle="No modules found"
      emptyDescription="Get started by creating your first module."
      createLabel="Create Module"
      onCreateClick={() => navigate('/admin/modules/create')}
      items={listPage.items}
      getRowKey={(module) => module.id}
      isLoading={listPage.isLoading}
      error={listPage.error}
      deleteModalTitle="Delete Module"
      deleteTarget={listPage.deleteTarget}
      getDeleteTargetName={(module) => module.name}
      deleteNoun="module"
      onDeleteCancel={listPage.cancelDelete}
      onDeleteConfirm={listPage.confirmDelete}
      isDeleting={listPage.isDeleting}
      isDeleteError={listPage.isDeleteError}
      deleteError={listPage.deleteError}
      onRowClick={(module) => navigate(`/admin/modules/${module.slug}/edit`)}
      renderHead={() => (
        <>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell align="right">Actions</Table.HeaderCell>
        </>
      )}
      renderRow={(module) => (
        <>
          <Table.Cell>
            <VStack gap={0}>
              <Text weight="medium">{module.name}</Text>
              {module.description && (
                <Text size="sm" color="muted">{module.description}</Text>
              )}
            </VStack>
          </Table.Cell>
          <Table.Cell>
            <Badge
              variant={module.is_active ? 'success' : 'default'}
              size="sm"
            >
              {module.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </Table.Cell>
          <Table.Cell align="right">
            <HStack gap={1} justify="end">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon icon={Pencil} size="xs" color="inherit" />}
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/modules/${module.slug}/edit`); }}
                aria-label={`Edit ${module.name}`}
              />
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon icon={Trash2} size="xs" color="inherit" />}
                onClick={(e) => { e.stopPropagation(); listPage.setDeleteTarget(module); }}
                aria-label={`Delete ${module.name}`}
              />
            </HStack>
          </Table.Cell>
        </>
      )}
    />
  );
}
